import React from "react";
import { Minus, Plus } from "@phosphor-icons/react";
import { Tooltip } from "@mui/material";

import {
  paneToolbarDisabledClass,
  paneToolbarInkClass,
} from "../../../FileViewer/paneToolbar";

const ZOOM_MIN = 50;
const ZOOM_MAX = 200;

interface ZoomControlsProps {
  zoomLevel: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
}

/** Reading zoom, as a pane-header control.
 *
 *  This used to ship a second `floating` placement that sat over the content's
 *  top-right corner. #11819 added the header placement for the artifact panes
 *  because the floating one covered data — a wide table lost its last column —
 *  but left every other surface on the float, so the document viewer and the
 *  interaction page kept the defect (#11852). Worse, the float only behaved as
 *  intended where it happened to be a child of the scroll container; in the
 *  document viewer it was a sibling, so `sticky` had nothing to stick to and
 *  the `overflow-auto` content box reflowed against it instead of scrolling
 *  under it. One placement, so there is one behaviour.
 *
 *  Bare controls, no box (#12481, Gerry, on the bordered groups: "really
 *  dont like this design"): the same grammar as the pane header beside it
 *  and the PDF viewer's reading-aids row.
 */
const ZoomControls: React.FC<ZoomControlsProps> = ({
  zoomLevel,
  onZoomIn,
  onZoomOut,
  onReset,
}) => {
  // Same disabled treatment as the buttons beside it in the pane header: an
  // end-of-ladder `+` used to keep its hover highlight while a disabled
  // Download next to it did not (#11852).
  const buttonClass = `flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-[7px] ${paneToolbarInkClass} ${paneToolbarDisabledClass}`;
  return (
    <div className="flex h-8 flex-none items-center">
      {/* describeChild: the title must not land as an aria-label on the
          wrapping span, a second element with the button's name. */}
      <Tooltip title="Zoom out" arrow placement="bottom" describeChild>
        <span className="flex">
          <button
            aria-label="Zoom out"
            onClick={onZoomOut}
            disabled={zoomLevel <= ZOOM_MIN}
            className={buttonClass}
          >
            <Minus size={16} />
          </button>
        </span>
      </Tooltip>
      <Tooltip title="Reset zoom" arrow placement="bottom">
        <button
          aria-label="Reset zoom"
          onClick={onReset}
          className={`flex h-8 min-w-[44px] cursor-pointer items-center justify-center rounded-[7px] px-1 text-[12.5px] tabular-nums ${paneToolbarInkClass}`}
        >
          {zoomLevel}%
        </button>
      </Tooltip>
      <Tooltip title="Zoom in" arrow placement="bottom" describeChild>
        <span className="flex">
          <button
            aria-label="Zoom in"
            onClick={onZoomIn}
            disabled={zoomLevel >= ZOOM_MAX}
            className={buttonClass}
          >
            <Plus size={16} />
          </button>
        </span>
      </Tooltip>
    </div>
  );
};

export default ZoomControls;
